import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  FormControl,
  FormHelperText,
  makeStyles,
  Typography
} from '@material-ui/core';
import Loader from '@components/framework/loader/Loader';
import {VerifyInsuranceFormType} from '@shared/modules/eligibility-verification/EligibilityVerificationFromType';
import {verifyInsuranceValidation} from '@shared/modules/eligibility-verification/EligibilityVerification';
import {Controller, SubmitHandler, useForm} from 'react-hook-form';
import StyledOutlinedInput from '@components/system/FormInput/StyledOutlinedInput';
import {APP_PRIMARY_COLOR} from 'src/style/variable';
import {useTranslation} from 'react-i18next';
import {useSnackbar} from 'notistack';
import React, {useState} from 'react';
import {
  verifyEligibility,
  createPatientInsurance,
  updatePatientInsurance
} from '@shared/services/InsuranceEligibilityServices';
import {SnackBarConfig} from 'src/utils/SnackBarConfig';
import {CommunicationService} from 'src/service/CommunicationService';
import {InsuranceTypes} from '@shared/constants/Mapping';
import CustomDropDown from '@components/system/CustomDropDown/CustomDropDown';

const styles = makeStyles(() => ({
  formControl: {
    width: '100%',
    marginBottom: 0
  },
  linkStyle: {
    whiteSpace: 'nowrap',
    lineHeight: '1.25rem',
    cursor: 'pointer',
    color: APP_PRIMARY_COLOR,
    textDecoration: 'underline'
  },
  resetTitle: {
    display: 'flex',
    left: 0,
    marginBottom: '0.5rem',
    // fontFamily: APP.FONT_FAMILY.MEDIUM,
    fontSize: '1rem'
  },
  insuranceClassParentDivWithOpacityStyles: {
    display: 'flex',
    flexDirection: 'column',
    '& .select-custom-clinic': {
      opacity: '0.6 !important'
    },
    '& .custom-select-error-styles': {
      opacity: '0.6 !important'
    }
  },
  insuranceClassParentDiv: {
    display: 'flex',
    flexDirection: 'column'
  },
  insuranceTypeClass: {
    marginBottom: '5px'
  }
}));

interface VerifyInsuranceModalProps {
  /* Need to add few things */
  isOpen: boolean;
  handleClose: () => void;
  clinicGroupId: string | number;
  clinicId: string | number;
  patientId: string | number;
  appointmentId?: string | number;
  onCancel: () => void;
  defaultInsuranceValues: any;
  /** 'add' | 'edit' = save to patient insurance (no appointment); 'verify' = run eligibility verification */
  mode?: 'add' | 'edit' | 'verify';
  onSuccess?: () => void;
}

const DEFAULT_VERIFY_INSURANCE_FIELD_VALUES: VerifyInsuranceFormType = {
  insurance_name: '',
  group_number: '',
  member_id: '',
  insurance_type: '',
  bin_number: '',
  process_control_number: '',
  rx_group_no: ''
};

type FormFieldType<T> = {
  key: T;
  label: string;
  placeholder: string;
  defaultValue?: string;
  required?: boolean;
  type?: 'number' | 'text';
};
type FormField<T extends Record<string, any>> = FormFieldType<keyof T>[];

const VerifyInsuranceFromFields: FormField<VerifyInsuranceFormType> = [
  {
    key: 'insurance_name',
    label: 'ELIGIBILITY_VERIFICATION.VERIFY_INSURANCE_FORM.INSURANCE_NAME',
    placeholder:
      'ELIGIBILITY_VERIFICATION.VERIFY_INSURANCE_FORM.INSURANCE_NAME_PLACEHOLDER',
    required: true,
    type: 'text'
  },
  {
    key: 'group_number',
    label:
      'ELIGIBILITY_VERIFICATION.VERIFY_INSURANCE_FORM.SUBSCRIBER_GROUP_NUMBER',
    placeholder:
      'ELIGIBILITY_VERIFICATION.VERIFY_INSURANCE_FORM.SUBSCRIBER_GROUP_NUMBER_PLACEHOLDER',
    required: true,
    type: 'text'
  },
  {
    key: 'member_id',
    label:
      'ELIGIBILITY_VERIFICATION.VERIFY_INSURANCE_FORM.SUBSCRIBER_MEMBER_ID',
    placeholder:
      'ELIGIBILITY_VERIFICATION.VERIFY_INSURANCE_FORM.SUBSCRIBER_MEMBER_ID_PLACEHOLDER',
    required: true,
    type: 'text'
  },
  {
    key: 'bin_number',
    label: 'ELIGIBILITY_VERIFICATION.VERIFY_INSURANCE_FORM.BIN_NUMBER',
    placeholder:
      'ELIGIBILITY_VERIFICATION.VERIFY_INSURANCE_FORM.BIN_NUMBER_PLACEHOLDER',
    required: false,
    type: 'text'
  },
  {
    key: 'process_control_number',
    label:
      'ELIGIBILITY_VERIFICATION.VERIFY_INSURANCE_FORM.PROCESS_CONTROL_NUMBER',
    placeholder:
      'ELIGIBILITY_VERIFICATION.VERIFY_INSURANCE_FORM.PROCESS_CONTROL_NUMBER_PLACEHOLDER',
    required: false,
    type: 'text'
  },
  {
    key: 'rx_group_no',
    label: 'ELIGIBILITY_VERIFICATION.VERIFY_INSURANCE_FORM.RX_GROUP_NUMBER',
    placeholder:
      'ELIGIBILITY_VERIFICATION.VERIFY_INSURANCE_FORM.RX_GROUP_NUMBER_PLACEHOLDER',
    required: false,
    type: 'text'
  }
];

const VerifyInsuranceModal = (
  props: VerifyInsuranceModalProps
): JSX.Element => {
  const {
    isOpen,
    handleClose,
    clinicGroupId,
    clinicId,
    patientId,
    appointmentId,
    onCancel,
    defaultInsuranceValues,
    mode = 'verify',
    onSuccess
  } = props;

  const isAddOrEdit = mode === 'add' || mode === 'edit';

  const [selectedInsuranceType, setSelectedInsuranceType] = useState(() => {
    return defaultInsuranceValues?.type
      ? {
          label:
            InsuranceTypes.find(
              item => item.value === defaultInsuranceValues.type
            )?.name ?? '',
          key:
            InsuranceTypes.find(
              item => item.value === defaultInsuranceValues.type
            )?.value ?? ''
        }
      : {label: '', key: ''};
  });

  const {
    control,
    formState: {errors},
    handleSubmit
  } = useForm<VerifyInsuranceFormType>({
    mode: 'onChange',
    defaultValues: {
      ...DEFAULT_VERIFY_INSURANCE_FIELD_VALUES,
      insurance_name: defaultInsuranceValues?.payer_name
        ? defaultInsuranceValues?.payer_name
        : DEFAULT_VERIFY_INSURANCE_FIELD_VALUES['insurance_name'],
      group_number: defaultInsuranceValues?.subscriber_group_no
        ? defaultInsuranceValues?.subscriber_group_no
        : DEFAULT_VERIFY_INSURANCE_FIELD_VALUES['group_number'],
      member_id: defaultInsuranceValues?.subscriber_member_no
        ? defaultInsuranceValues?.subscriber_member_no
        : DEFAULT_VERIFY_INSURANCE_FIELD_VALUES['member_id'],
      insurance_type: selectedInsuranceType?.key,
      bin_number: defaultInsuranceValues?.bin_number
        ? defaultInsuranceValues?.bin_number
        : DEFAULT_VERIFY_INSURANCE_FIELD_VALUES['bin_number'],
      process_control_number: defaultInsuranceValues?.process_control_number
        ? defaultInsuranceValues?.process_control_number
        : DEFAULT_VERIFY_INSURANCE_FIELD_VALUES['process_control_number'],
      rx_group_no: defaultInsuranceValues?.rx_group_no
        ? defaultInsuranceValues?.rx_group_no
        : DEFAULT_VERIFY_INSURANCE_FIELD_VALUES['rx_group_no']
    },
    resolver: values => {
      const formError = verifyInsuranceValidation(values);
      const hasError = Object.keys(formError).length > 0;
      return {
        values: hasError ? {} : values,
        errors: hasError ? formError : {}
      };
    }
  });
  const classes = styles();
  const {t} = useTranslation();
  const {enqueueSnackbar} = useSnackbar();
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const handleFormSubmit: SubmitHandler<VerifyInsuranceFormType> = async data => {
    try {
      setIsSubmitting(true);
      if (isAddOrEdit) {
        const payload = {
          payer_name: data.insurance_name?.trim(),
          subscriber_group_no: (data.group_number ?? '').trim() || undefined,
          subscriber_member_no: (data.member_id ?? '').trim(),
          type: selectedInsuranceType?.key || undefined,
          bin_number: (data.bin_number ?? '').trim() || undefined,
          process_control_number:
            (data.process_control_number ?? '').trim() || undefined,
          rx_group_no: (data.rx_group_no ?? '').trim() || undefined
        };
        if (mode === 'edit' && defaultInsuranceValues?.id) {
          const updatePayload = {
            ...payload,
            priority: defaultInsuranceValues?.priority
          };
          const updated = await updatePatientInsurance(
            clinicGroupId,
            patientId,
            defaultInsuranceValues.id,
            updatePayload
          );
          enqueueSnackbar(
            updated?.message ||
              t('PATIENT_DETAILS_DRAWER.INSURANCE_MODULE.INSURANCE_UPDATED') ||
              'Insurance updated',
            SnackBarConfig('s')
          );
        } else {
          const created = await createPatientInsurance(
            clinicGroupId,
            patientId,
            payload
          );
          enqueueSnackbar(
            created?.message ||
              t('PATIENT_DETAILS_DRAWER.INSURANCE_MODULE.INSURANCE_ADDED') ||
              'Insurance added',
            SnackBarConfig('s')
          );
        }
        onSuccess?.();
        handleClose();
        return;
      }
      const payload = {
        patient_id: patientId,
        insurance_name: data.insurance_name,
        member_id: data.member_id,
        group_number: data.group_number,
        insurance_type: selectedInsuranceType?.key,
        bin_number: (data.bin_number ?? '').trim(),
        process_control_number: (data.process_control_number ?? '').trim(),
        rx_group_no: (data.rx_group_no ?? '').trim(),
        ...(defaultInsuranceValues?.id && {
          insurance_id: defaultInsuranceValues.id
        })
      };
      const res = await verifyEligibility(
        clinicGroupId,
        clinicId,
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        appointmentId!,
        payload
      );
      enqueueSnackbar(res?.message, SnackBarConfig('s'));
      CommunicationService.sendEvent({
        action: 'REFRESH_APPOINTMENT',
        data: {
          clinicGroupId,
          clinicId,
          appointmentId: appointmentId
        }
      });
      // If it succeeds then close the modal
      handleClose();
    } catch (error: any) {
      enqueueSnackbar(
        error?.message
          ? error.message
          : t('ELIGIBILITY_VERIFICATION.ERROR_IN_VERIFYING_INSURANCE_DETAILS'),
        SnackBarConfig('e')
      );
    } finally {
      setIsSubmitting(false);
    }
  };
  return (
    <Dialog
      open={isOpen}
      fullWidth={true}
      maxWidth={'sm'}
      disableBackdropClick={true}
      disableEscapeKeyDown={true}>
      <DialogTitle disableTypography>
        {isAddOrEdit
          ? mode === 'edit'
            ? t('PATIENT_DETAILS_DRAWER.INSURANCE_MODULE.EDIT_INSURANCE')
            : t('PATIENT_DETAILS_DRAWER.INSURANCE_MODULE.ADD_INSURANCE')
          : t(
              'ELIGIBILITY_VERIFICATION.VERIFY_INSURANCE_FORM.VERIFY_INSURANCE_ELIGIBILITY'
            )}
      </DialogTitle>
      <DialogContent dividers>
        {VerifyInsuranceFromFields.map(formField => (
          <Controller
            key={formField.key}
            name={formField.key}
            control={control}
            render={field => (
              <FormControl
                className={classes.formControl}
                error={!!errors[formField.key]}>
                <p>
                  {t(formField.label)}
                  {formField.required && `*`}
                </p>
                <StyledOutlinedInput
                  margin={'dense'}
                  {...field}
                  placeholder={t(formField.placeholder)}
                  type={formField.type ?? 'text'}
                />
                {errors?.[formField.key]?.message && (
                  <FormHelperText>
                    {t(errors?.[formField.key]?.message as string)}
                  </FormHelperText>
                )}
              </FormControl>
            )}
          />
        ))}
        <div
          className={
            selectedInsuranceType?.label === ''
              ? classes.insuranceClassParentDivWithOpacityStyles
              : classes.insuranceClassParentDiv
          }>
          <FormControl
            error={!!errors.insurance_type}
            fullWidth
            variant="outlined">
            <Typography className={classes.resetTitle}>
              {t('ELIGIBILITY_VERIFICATION.INSURANCE_TYPES')}*
            </Typography>
            <Controller
              id="insurance_type"
              control={control}
              name="insurance_type"
              error={!!errors?.insurance_type}
              render={({onChange, onBlur, value}): React.ReactElement => (
                <CustomDropDown
                  dropDownList={InsuranceTypes.map(insuranceType => ({
                    label: t(insuranceType.name),
                    key: insuranceType.value
                  }))}
                  placeHolderText={t(
                    'ELIGIBILITY_VERIFICATION.SELECT_INSURANCE_TYPE'
                  )}
                  dropDownHeaderText={t(
                    'ELIGIBILITY_VERIFICATION.INSURANCE_TYPES'
                  )}
                  disabled={false}
                  clear={true}
                  clearAction={() => {
                    setSelectedInsuranceType({
                      label: '',
                      key: ''
                    });
                    onChange(''); // Reset form value
                  }}
                  handleAction={insuranceType => {
                    setSelectedInsuranceType({
                      label: insuranceType?.label,
                      key: insuranceType?.key
                    });
                    onChange(insuranceType.key); // Update form value
                  }}
                  defaultValue={
                    selectedInsuranceType?.label === ''
                      ? t('ELIGIBILITY_VERIFICATION.SELECT_INSURANCE_TYPE')
                      : t(selectedInsuranceType?.label)
                  }
                  opacityProp={true}
                  error={errors.insurance_type ? true : false}
                />
              )}
            />
            {errors?.insurance_type?.message && (
              <FormHelperText>
                {t(errors?.insurance_type?.message as string)}
              </FormHelperText>
            )}
          </FormControl>
        </div>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => {
            handleClose();
            onCancel();
          }}
          color="primary"
          disabled={isSubmitting}>
          {t('ELIGIBILITY_VERIFICATION.VERIFY_INSURANCE_FORM.CANCEL')}
        </Button>
        <Button
          onClick={handleSubmit(handleFormSubmit)}
          color="primary"
          variant="contained"
          disabled={isSubmitting}>
          {!isSubmitting ? (
            isAddOrEdit ? (
              mode === 'add' ? (
                t('ELIGIBILITY_VERIFICATION.VERIFY_INSURANCE_FORM.ADD')
              ) : (
                t('ELIGIBILITY_VERIFICATION.VERIFY_INSURANCE_FORM.UPDATE')
              )
            ) : (
              t('ELIGIBILITY_VERIFICATION.VERIFY_INSURANCE_FORM.VERIFY')
            )
          ) : (
            <Loader />
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default VerifyInsuranceModal;
